import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Form,
  FormLayout,
  Modal,
  TextField,
  Autocomplete,
  Card,
  Banner,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { fetch as fetchProducts } from "containers/products/products.service";
import { fetchById, post, patch } from "services/product-groups.service";

type ProductOption = {
  value: string;
  label: string;
};

type SelectedProduct = {
  id: string;
  label: string;
};

const formatProductLabel = (product: any) => {
  const codes = [product?.sku, product?.supplierSku].filter(
    (value): value is string => Boolean(value)
  );
  const codesSuffix = codes.length ? ` [${codes.join(" / ")}]` : "";
  return `${product?.title || ""}${codesSuffix}`;
};

// Map a group's product objects (from the list row or the detail fetch) into
// the {id,label} shape the selected-products list renders.
const toSelectedProducts = (products?: any[]): SelectedProduct[] =>
  (products || [])
    .filter((p) => p?.id)
    .map((p) => ({ id: p.id, label: formatProductLabel(p) }));

// Placeholder rows for the selected-products list while the full group (with
// its products) loads — keeps the form visible instead of swapping it for a
// spinner, so opening the modal no longer blinks.
const ProductsListSkeleton = () => (
  <div className="animate-pulse flex flex-col" aria-hidden="true">
    {Array.from({ length: 3 }).map((_, i) => (
      <div
        key={i}
        className="flex items-center justify-between py-3 border-b border-[#F2F1EF] last:border-b-0"
      >
        <div className="h-3 w-2/3 rounded bg-gray-200" />
        <div className="h-3 w-12 rounded bg-gray-100" />
      </div>
    ))}
  </div>
);

type ProductGroupFormProps = {
  companyId: string;
  groupId: string | null;
  initialGroup?: {
    id: string;
    name?: string;
    code?: string | null;
    description?: string;
    products?: any[];
  } | null;
  open: boolean;
  onClose: () => void;
  /** Receives the edited group's id (undefined when a new group was created). */
  onSaved?: (savedGroupId?: string) => void;
};

function ProductGroupForm({
  companyId,
  groupId,
  initialGroup,
  open,
  onClose,
  onSaved,
}: ProductGroupFormProps) {
  const { t } = useTranslation();
  const [name, setName] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  // Selected products are kept as full {id,label} objects (not just ids resolved
  // against the searchable options), so they render instantly from the row data
  // and don't disappear/flash when the search options reload.
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>(
    []
  );
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [query, setQuery] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState<boolean>(false);
  const [isLoadingGroup, setIsLoadingGroup] = useState<boolean>(false);
  // Id of the group whose authoritative data has finished loading — used to
  // decide whether to show the products skeleton (only when we have nothing to
  // show yet).
  const [loadedGroupId, setLoadedGroupId] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const latestProductsRequestIdRef = useRef(0);

  const loadProducts = useCallback(
    async (search?: string) => {
      // Track this request so that only the latest one can turn off the loading state.
      const requestId = ++latestProductsRequestIdRef.current;
      setIsLoadingProducts(true);

      try {
        const filtersValue = search ? { textSearch: search } : {};
        const { productsCollection } = await fetchProducts(companyId, filtersValue);
        const options: ProductOption[] = productsCollection.map((p: any) => ({
          value: p.id,
          label: formatProductLabel(p),
        }));
        setProductOptions(options);
      } catch (error) {
        // Ignore cancellation errors - they're expected when a new search is triggered
        if (axios.isCancel(error) || (error as any)?.message?.includes("canceled")) {
          return;
        }
        // Re-throw other errors
        throw error;
      } finally {
        // Only the latest in-flight request is allowed to clear the loading flag.
        if (latestProductsRequestIdRef.current === requestId) {
          setIsLoadingProducts(false);
        }
      }
    },
    [companyId]
  );

  // Paint the form on the FIRST frame when the modal opens: reset the product
  // search, prefill name/code/description, and seed the selected products from
  // the clicked row while the authoritative detail fetch runs.
  useLayoutEffect(() => {
    if (!open) return;
    setQuery("");
    if (groupId) {
      setLoadedGroupId(null);
    }
    if (groupId && initialGroup) {
      setName(initialGroup.name || "");
      setCode(initialGroup.code || "");
      setDescription(initialGroup.description || "");
      setSelectedProducts(toSelectedProducts(initialGroup.products));
    } else if (!groupId) {
      // Reset when creating a new group
      setName("");
      setCode("");
      setDescription("");
      setSelectedProducts([]);
    }
  }, [groupId, initialGroup, open]);

  // Always fetch the full group when editing so saves use authoritative product
  // membership; initialGroup is only an immediate, paint-friendly seed.
  useEffect(() => {
    let isCurrent = true;

    const loadGroup = async () => {
      if (!groupId || !companyId) return;
      try {
        setIsLoadingGroup(true);
        setLoadError(null);
        const { productGroupEntity } = await fetchById(companyId, groupId);
        if (!isCurrent) return;
        setName(productGroupEntity.name || "");
        setCode(productGroupEntity.code || "");
        setDescription(productGroupEntity.description || "");
        setSelectedProducts(toSelectedProducts(productGroupEntity.products));
      } catch (error) {
        if (!isCurrent) return;
        setLoadError("error_getting_data");
      } finally {
        if (!isCurrent) return;
        setIsLoadingGroup(false);
        setLoadedGroupId(groupId);
      }
    };
    loadGroup();

    return () => {
      isCurrent = false;
    };
  }, [companyId, groupId]);

  const handleSearchChange = useCallback(
    async (value: string) => {
      setQuery(value);
      await loadProducts(value);
    },
    [loadProducts]
  );

  // Load the searchable product options lazily, only when the user first opens
  // the search — so opening the modal itself triggers no product fetch (and no
  // post-open re-render). The selected products already render from the seeded
  // row data, independent of these options.
  const handleProductsFocus = useCallback(() => {
    if (productOptions.length === 0 && !isLoadingProducts) {
      loadProducts();
    }
  }, [productOptions.length, isLoadingProducts, loadProducts]);

  const handleSelectProducts = useCallback(
    (selected: string[]) => {
      setSelectedProducts((prev) => {
        const existing = new Set(prev.map((p) => p.id));
        const additions = selected
          .filter((id) => !existing.has(id))
          .map((id) => {
            const option = productOptions.find((o) => o.value === id);
            return option ? { id, label: option.label } : null;
          })
          .filter((p): p is SelectedProduct => !!p);
        return [...prev, ...additions];
      });
      setQuery("");
    },
    [productOptions]
  );

  const handleRemoveProduct = useCallback((id: string) => {
    setSelectedProducts((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const isLoadingGroupDetail =
    groupId !== null && (isLoadingGroup || loadedGroupId !== groupId);
  const isBusy = isSaving || isLoadingGroupDetail;

  const handleSave = async () => {
    const trimmedName = name.trim();
    const trimmedCode = code.trim();
    if (
      !trimmedName ||
      !trimmedCode ||
      !companyId ||
      loadError ||
      isLoadingGroupDetail
    ) {
      return;
    }
    setIsSaving(true);
    setErrMsg(null);
    try {
      const payload = {
        name: trimmedName,
        code: trimmedCode,
        description,
        products: selectedProducts.map((p) => p.id),
      };
      if (groupId) {
        await patch(companyId, groupId, payload);
      } else {
        await post(companyId, payload);
      }
      // Pass the edited id so the list can refresh just that row in place;
      // undefined for a new group (the list reloads its first page).
      if (onSaved) onSaved(groupId ?? undefined);
      onClose();
    } catch (error) {
      setErrMsg("error");
    } finally {
      setIsSaving(false);
    }
  };

  // Show the products skeleton only when editing and we have nothing to show
  // yet (no row data seeded and the detail fetch hasn't returned). When the row
  // already provided products they render immediately — no skeleton, no blink.
  const showProductsSkeleton =
    !!groupId &&
    loadedGroupId !== groupId &&
    selectedProducts.length === 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={groupId ? t("edit_product_group") : t("create_product_group")}
      primaryAction={{
        content: t("save"),
        onAction: handleSave,
        loading: isSaving,
        disabled: !name.trim() || !code.trim() || isBusy || !!loadError,
      }}
      secondaryActions={[
        {
          content: t("cancel"),
          onAction: onClose,
          disabled: isBusy,
        },
      ]}
    >
      <Modal.Section>
        <Form onSubmit={handleSave}>
          <FormLayout>
            {loadError && <Banner tone="critical">{t(loadError)}</Banner>}
            {errMsg && <Banner tone="critical">{t(errMsg)}</Banner>}
            <TextField
              label={t("name")}
              value={name}
              autoComplete="off"
              onChange={(value) => setName(value)}
              requiredIndicator
            />
            <TextField
              label={t("code")}
              value={code}
              autoComplete="off"
              onChange={(value) => setCode(value)}
              helpText={t("product_group_code_help")}
            />
            <Card>
              <Autocomplete
                options={productOptions}
                selected={[]}
                loading={isLoadingProducts}
                onSelect={handleSelectProducts}
                textField={
                  <Autocomplete.TextField
                    label={t("search_products")}
                    value={query}
                    autoComplete="off"
                    onChange={handleSearchChange}
                    onFocus={handleProductsFocus}
                  />
                }
              />
              {showProductsSkeleton ? (
                <ProductsListSkeleton />
              ) : (
                <ul className="divide-y divide-[var(--p-color-border-secondary)]">
                  {selectedProducts.map((item) => (
                    <li
                      key={item.id}
                      className="flex items-center justify-between gap-3 py-1"
                    >
                      <span className="min-w-0 break-words text-sm">
                        {item.label}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveProduct(item.id)}
                        className="shrink-0 rounded-md px-3 py-2 text-sm font-medium text-[var(--p-color-text-critical)] hover:bg-red-50 max-md:min-h-11"
                      >
                        {t("remove")}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <TextField
              label={t("description")}
              value={description}
              autoComplete="off"
              onChange={(value) => setDescription(value)}
              multiline={3}
            />
          </FormLayout>
        </Form>
      </Modal.Section>
    </Modal>
  );
}

export default ProductGroupForm;


